/**
 * Privacy dashboard — "Your data" (§15).
 *
 * Shows the account holder, in plain language, what KinCode holds about them, and
 * gives them the two controls the regulation requires: **export** everything held,
 * and **delete** the account. Deletion is a 30-day recoverable process — retrieval
 * stops and the subscription is cancelled at once, but the data is kept so an
 * accidental deletion (which here would cut a child off from their contacts) can be
 * undone. None of this is enforced here; the server is the authority (§5).
 */

import { el, icon } from "../ui/dom.js";
import { errorAlert, pageHeader, section, successAlert } from "../ui/patterns.js";

/** Mirrors `DELETION_GRACE_DAYS` in `functions/src/shared/config.js`. */
const GRACE_DAYS = 30;
const DAY = 24 * 60 * 60 * 1000;

/**
 * @param {number} ms
 * @returns {string}
 */
function formatDate(ms) {
  return new Date(ms).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * Offer a JSON object to the browser as a file download.
 *
 * @param {object} data
 */
function downloadJson(data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = el("a", { href: url, download: "kincode-data.json" });
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** @type {import("../app/router.js").PageRenderer} */
export default function renderYourData(context) {
  const account = context.services.auth.getCurrentAccount();
  const frag = document.createDocumentFragment();
  frag.append(pageHeader("Your data", "What KinCode holds about you, and your control over it."));
  if (!account) {
    return frag;
  }

  const feedback = el("div", { "aria-live": "assertive" });
  frag.append(feedback);

  const pending = account.accountStatus === "pendingDeletion";
  if (pending) {
    frag.append(pendingBanner(context, account, feedback));
  }

  frag.append(whatWeHold(context, account));
  frag.append(exportSection(context, feedback));
  if (!pending) {
    frag.append(deleteSection(context, feedback));
  }

  return frag;
}

/**
 * @param {import("../app/router.js").RouteContext} context
 * @param {import("../types/models.js").Account} account
 * @returns {HTMLElement}
 */
function whatWeHold(context, account) {
  const profileCount = el("span", {}, ["…"]);
  // Fill in the count once the profiles load; a read failure leaves the ellipsis.
  context.services.profiles
    .list()
    .then((profiles) => {
      profileCount.textContent = String(profiles.length);
    })
    .catch(() => {});

  return section("What KinCode holds about you", [
    el("dl", { class: "kin-details" }, [
      el("dt", {}, ["Email"]),
      el("dd", {}, [account.email]),
      el("dt", {}, ["Plan"]),
      el("dd", {}, [account.planId ?? "free"]),
      el("dt", {}, ["Recovery profiles"]),
      el("dd", {}, [profileCount]),
    ]),
    el("ul", { class: "kin-plain-list" }, [
      el("li", {}, ["We never hold a child's name, date of birth, school, address or location."]),
      el("li", {}, ["Contact names and telephone numbers are encrypted before storage."]),
      el("li", {}, ["We keep no record of when anyone retrieves a set of contacts."]),
      el("li", {}, ["Abuse-prevention data is pseudonymised and short-lived."]),
    ]),
  ]);
}

/**
 * @param {import("../app/router.js").RouteContext} context
 * @param {HTMLElement} feedback
 * @returns {HTMLElement}
 */
function exportSection(context, feedback) {
  const button = el(
    "wa-button",
    {
      appearance: "outlined",
      on: {
        click: async () => {
          feedback.replaceChildren();
          try {
            const data = await context.services.account.exportData();
            downloadJson(data);
            feedback.replaceChildren(successAlert("Your data has been downloaded."));
          } catch (error) {
            feedback.replaceChildren(
              errorAlert(
                error instanceof Error ? error.message : "Could not export your data. Try again.",
              ),
            );
          }
        },
      },
    },
    [icon("shield-halved"), "Download my data (JSON)"],
  );

  return section("Export your data", [
    el("p", { class: "kin-note" }, [
      "Download everything KinCode holds about your account — your details and every " +
        "recovery profile, including its phrase and contacts.",
    ]),
    button,
  ]);
}

/**
 * @param {import("../app/router.js").RouteContext} context
 * @param {HTMLElement} feedback
 * @returns {HTMLElement}
 */
function deleteSection(context, feedback) {
  const dialog = /** @type {HTMLElement & { open: boolean }} */ (
    el("wa-dialog", { label: "Delete your account?" }, [
      el("p", {}, [
        "This disables retrieval immediately and cancels any subscription. Your account " +
          "and all recovery profiles are permanently erased after 30 days. You can cancel " +
          "within those 30 days to restore everything.",
      ]),
      el("div", { slot: "footer", class: "kin-actions" }, [
        el("wa-button", { appearance: "outlined", on: { click: () => (dialog.open = false) } }, [
          "Keep my account",
        ]),
        el(
          "wa-button",
          {
            variant: "danger",
            appearance: "filled",
            on: {
              click: async () => {
                feedback.replaceChildren();
                try {
                  await context.services.account.requestDeletion();
                  dialog.open = false;
                  // Re-read the account so the pending banner replaces this section.
                  await context.services.auth.refresh();
                } catch (error) {
                  dialog.open = false;
                  feedback.replaceChildren(
                    errorAlert(
                      error instanceof Error
                        ? error.message
                        : "Could not start deletion. Try again.",
                    ),
                  );
                }
              },
            },
          },
          [icon("trash"), "Yes, delete my account"],
        ),
      ]),
    ])
  );

  return section("Delete your account", [
    el("p", { class: "kin-note" }, [
      "Deletion stops retrieval straight away, cancels any subscription, and erases " +
        "your account after a 30-day recovery window.",
    ]),
    el(
      "wa-button",
      {
        variant: "danger",
        appearance: "outlined",
        on: { click: () => (dialog.open = true) },
      },
      [icon("trash"), "Delete my account"],
    ),
    dialog,
  ]);
}

/**
 * @param {import("../app/router.js").RouteContext} context
 * @param {import("../types/models.js").Account} account
 * @param {HTMLElement} feedback
 * @returns {HTMLElement}
 */
function pendingBanner(context, account, feedback) {
  const deletesAt = (account.deletionRequestedAt ?? Date.now()) + GRACE_DAYS * DAY;
  const cancelButton = el(
    "wa-button",
    {
      variant: "brand",
      appearance: "filled",
      on: {
        click: async () => {
          feedback.replaceChildren();
          try {
            await context.services.account.cancelDeletion();
            await context.services.auth.refresh();
            feedback.replaceChildren(successAlert("Your account deletion has been cancelled."));
          } catch (error) {
            feedback.replaceChildren(
              errorAlert(
                error instanceof Error ? error.message : "Could not cancel deletion. Try again.",
              ),
            );
          }
        },
      },
    },
    [icon("circle-check"), "Cancel deletion"],
  );

  return section("Scheduled for deletion", [
    errorAlert(
      `This account is scheduled for permanent deletion on ${formatDate(deletesAt)}. ` +
        "Retrieval is already disabled. You can cancel until then to restore everything.",
    ),
    cancelButton,
  ]);
}
